import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users, Plus, MoreVertical, UserMinus, Shield, Loader2, Lock, 
  Mail, Link2, Copy, Check, Clock, XCircle, CheckCircle2, Trash2 
} from 'lucide-react';
import { toast } from 'sonner';
import { orgRoleBadgeClass, formatOrgRoleLabel } from '../lib/orgRoles';
import { API_URL } from '../config';

const INVITE_STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  accepted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const MembersPage = () => {
  const { user, currentOrg } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  /** Access for invited spaces: view = read-only, edit = can_edit_spaces */
  const [inviteAccess, setInviteAccess] = useState('view');
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [updating, setUpdating] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [createdInvite, setCreatedInvite] = useState(null); // For showing the generated link

  const canManageMembers = currentOrg?.user_role === 'company';
  const isCompanyAdmin = currentOrg?.user_role === 'company';

  const fetchMembers = useCallback(async () => {
    if (!currentOrg) return;
    
    try {
      const response = await axios.get(`${API_URL}/organizations/${currentOrg.id}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  }, [currentOrg]);

  const fetchInvites = useCallback(async () => {
    if (!currentOrg || !canManageMembers) return;
    
    try {
      const response = await axios.get(`${API_URL}/organizations/${currentOrg.id}/invites`);
      setInvites(response.data);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  }, [currentOrg, canManageMembers]);

  const fetchSpaces = useCallback(async () => {
    if (!currentOrg) return;
    
    try {
      const response = await axios.get(`${API_URL}/organizations/${currentOrg.id}/spaces`);
      setSpaces(response.data);
    } catch (error) {
      console.error('Failed to fetch spaces:', error);
    }
  }, [currentOrg]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchMembers(), fetchInvites(), fetchSpaces()]);
    setLoading(false);
  }, [fetchMembers, fetchInvites, fetchSpaces]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      const response = await axios.post(`${API_URL}/organizations/${currentOrg.id}/invites`, {
        email: inviteEmail,
        role: 'member',
        spaces: selectedSpaces.length > 0 ? selectedSpaces : null,
        can_edit_spaces: inviteAccess === 'edit',
      });
      
      // Show the created invite with link
      setCreatedInvite(response.data);
      setInvites(prev => [...prev, response.data]);
      setInviteEmail('');
      setInviteAccess('view');
      setSelectedSpaces([]);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create invite';
      toast.error(message);
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLink = async (inviteUrl, inviteId) => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedLink(inviteId);
      toast.success('Invite link copied!');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API_URL}/organizations/${currentOrg.id}/invites/${inviteId}`);
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success('Invite deleted');
    } catch (error) {
      toast.error('Failed to delete invite');
    }
  };

  const handleUpdateRole = async () => {
    if (!editMember) return;

    setUpdating(true);
    try {
      await axios.put(`${API_URL}/organizations/${currentOrg.id}/members/${editMember.id}`, {
        role: editRole,
      });
      toast.success('Role updated successfully');
      setEditMember(null);
      fetchMembers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update role';
      toast.error(message);
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.user_name} from the workspace?`)) return;

    try {
      await axios.delete(`${API_URL}/organizations/${currentOrg.id}/members/${member.id}`);
      toast.success('Member removed');
      fetchMembers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove member';
      toast.error(message);
    }
  };

  const toggleSpaceSelection = (spaceId) => {
    setSelectedSpaces(prev => 
      prev.includes(spaceId) 
        ? prev.filter(id => id !== spaceId)
        : [...prev, spaceId]
    );
  };

  if (!currentOrg) {
    return (
      <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="members-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">Members</h1>
          <p className="text-slate-600 dark:text-slate-400">Select a workspace to manage members</p>
        </div>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No Workspace Selected</h3>
            <p className="text-slate-600 dark:text-slate-400">Please select a workspace from the sidebar.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingInvites = invites.filter(i => i.status === 'pending');

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="members-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">Members</h1>
          <p className="text-slate-600 dark:text-slate-400">Manage your workspace team members</p>
        </div>
        {canManageMembers && (
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => setShowInvite(true)}
            data-testid="invite-member-button"
          >
            <Mail className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Members ({members.length})
          </TabsTrigger>
          {canManageMembers && (
            <TabsTrigger value="invites" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Invites
              {pendingInvites.length > 0 && (
                <Badge className="ml-1 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                  {pendingInvites.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">Team Members</CardTitle>
              <CardDescription>{members.length} member{members.length !== 1 ? 's' : ''} in this workspace</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                </div>
              ) : members.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      {canManageMembers && <TableHead className="w-10"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {member.user_name?.charAt(0).toUpperCase() || 'U'}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-slate-900 dark:text-white">
                                {member.user_name}
                                {member.user_id === user?.id && (
                                  <span className="ml-2 text-xs text-slate-500">(You)</span>
                                )}
                              </div>
                              <div className="text-sm text-slate-500">{member.user_email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={orgRoleBadgeClass(member.role)}>
                            {formatOrgRoleLabel(member.role)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-600 dark:text-slate-400">
                          {new Date(member.joined_at).toLocaleDateString()}
                        </TableCell>
                        {canManageMembers && (
                          <TableCell>
                            {member.user_id !== user?.id &&
                              (member.role !== 'company' || isCompanyAdmin) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`member-menu-${member.id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => { setEditMember(member); setEditRole(member.role); }}>
                                    <Shield className="w-4 h-4 mr-2" />
                                    Change Role
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleRemoveMember(member)} className="text-red-600 dark:text-red-400">
                                    <UserMinus className="w-4 h-4 mr-2" />
                                    Remove Member
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-slate-500">No members found</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invites Tab */}
        {canManageMembers && (
          <TabsContent value="invites">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">Pending Invites</CardTitle>
                <CardDescription>Manage invitation links for new team members</CardDescription>
              </CardHeader>
              <CardContent>
                {invites.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="w-20">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invites.map((invite) => (
                        <TableRow key={invite.id} data-testid={`invite-row-${invite.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-900 dark:text-white">{invite.email}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={orgRoleBadgeClass(invite.role)}>
                              {formatOrgRoleLabel(invite.role)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={INVITE_STATUS_COLORS[invite.status]}>
                              {invite.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                              {invite.status === 'accepted' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {invite.status === 'expired' && <XCircle className="w-3 h-3 mr-1" />}
                              {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600 dark:text-slate-400">
                            {new Date(invite.expires_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {invite.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCopyLink(invite.invite_url, invite.id)}
                                  title="Copy invite link"
                                >
                                  {copiedLink === invite.id ? (
                                    <Check className="w-4 h-4 text-emerald-600" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteInvite(invite.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Delete invite"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-8 text-center text-slate-500">
                    <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No invites yet. Click "Invite Member" to create one.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Role Permissions Info */}
      <Card className="mt-6 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-[Manrope] text-slate-900 dark:text-white">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm">
            <div className="flex items-center gap-3">
              <Badge className={orgRoleBadgeClass('company')}>Company</Badge>
              <span className="text-slate-600 dark:text-slate-400">
                Client workspace admins — billing, settings, members, all spaces
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={orgRoleBadgeClass('member')}>Member</Badge>
              <span className="text-slate-600 dark:text-slate-400">
                Invited users — access and edit rights follow space assignments
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invite Member Dialog */}
      <Dialog open={showInvite} onOpenChange={(open) => {
        setShowInvite(open);
        if (!open) {
          setCreatedInvite(null);
          setInviteEmail('');
          setInviteAccess('view');
          setSelectedSpaces([]);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {createdInvite ? 'Invite Created!' : 'Invite Team Member'}
            </DialogTitle>
            <DialogDescription>
              {createdInvite 
                ? 'Share the link below with your team member to invite them.' 
                : 'Send an invite link via email. The recipient will create an account to join.'}
            </DialogDescription>
          </DialogHeader>
          
          {createdInvite ? (
            <div className="py-4 space-y-4">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-medium text-emerald-800 dark:text-emerald-300">Invite sent to {createdInvite.email}</span>
                </div>
                <p className="text-sm text-emerald-700 dark:text-emerald-400">
                  An email has been sent. You can also share the link directly:
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Invite Link</Label>
                <div className="flex gap-2">
                  <Input
                    value={createdInvite.invite_url}
                    readOnly
                    className="bg-slate-50 dark:bg-slate-800 font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleCopyLink(createdInvite.invite_url, createdInvite.id)}
                    className="flex-shrink-0"
                  >
                    {copiedLink === createdInvite.id ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  This link expires in 7 days. The user will join as{' '}
                  <Badge className={orgRoleBadgeClass(createdInvite.role)}>
                    {formatOrgRoleLabel(createdInvite.role)}
                  </Badge>
                </p>
              </div>
              
              <DialogFooter>
                <Button 
                  onClick={() => {
                    setShowInvite(false);
                    setCreatedInvite(null);
                    setActiveTab('invites');
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleInvite}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-access">Member access</Label>
                <Select value={inviteAccess} onValueChange={setInviteAccess}>
                  <SelectTrigger id="invite-access" data-testid="invite-access-select">
                    <SelectValue placeholder="Select access" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View — read-only in selected spaces</SelectItem>
                    <SelectItem value="edit">Edit — create &amp; edit docs in selected spaces</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Invited users always join with the Member role.</p>
              </div>
              {spaces.length > 0 && (
                <div className="space-y-2">
                  <Label>Grant Access to Spaces (Optional)</Label>
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 max-h-40 overflow-auto">
                    {spaces.map(space => (
                      <label key={space.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-2 -mx-2">
                        <input
                          type="checkbox"
                          checked={selectedSpaces.includes(space.id)}
                          onChange={() => toggleSpaceSelection(space.id)}
                          className="rounded"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{space.name}</span>
                        {space.visibility === 'private' && <Lock className="w-3 h-3 text-slate-400" />}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">Selected members will have immediate access to these spaces</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInvite(false)} disabled={inviting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={inviting || !inviteEmail.trim()}
                data-testid="invite-submit-button"
              >
                {inviting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Link2 className="mr-2 h-4 w-4" />
                    Generate Invite Link
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editMember} onOpenChange={(open) => !open && setEditMember(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Change Role</DialogTitle>
            <DialogDescription>Update the role for {editMember?.user_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger id="edit-role" data-testid="edit-role-select">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  {isCompanyAdmin && <SelectItem value="company">Company</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditMember(null)} disabled={updating}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={updating || editRole === editMember?.role}
              data-testid="update-role-button"
            >
              {updating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersPage;
